// @ts-expect-error React import required to render in Jira SSR
import React from 'react';
import {
	type DefaultMessagesResponse,
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import type { EligibleMessage as BackendMessageBody } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import type { SerializableRecord } from '@post-office/serializable-record';

import { usePlacementApi } from '@post-office/use-placement-api';

import { useEffect, useState } from 'react';

import type { AdminAgentLearnMoreAction, AdminAgentSetupAction } from './types';

export type {
	AdminAgentProjectDetails,
	AdminAgentSetupSelection,
	AdminAgentSetupAction,
	AdminAgentLearnMoreAction,
} from './types';

export type PlacementRenderProps = {
	/** Opens the selected agent and project in the host product. */
	onSetUp?: AdminAgentSetupAction;
	/** Opens the host product's "how it works" dialog. */
	onLearnMore?: AdminAgentLearnMoreAction;
	/** Called by message template to dismiss itself from the UI */
	dismiss: () => void;
};

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

export type AdminAgentRecommendationStatus = 'loading' | 'done';

export type AdminAgentRecommendationProps = {
	onSetUp?: AdminAgentSetupAction;
	/**
	 * Opens the host product's "how it works" dialog. When omitted, the message
	 * template hides its "learn more" control rather than rendering a dead one.
	 */
	onLearnMore?: AdminAgentLearnMoreAction;
	preloadedInitialData?: DefaultMessagesResponse | undefined;
	/**
	 * Called with the number of messages this placement will render whenever
	 * that count changes (after the API load completes and on every dismiss),
	 * and the current loading status of the placement.
	 *
	 * Use this in the Jira-side adapter to drive things like whether the
	 * "Recommended" empty state should be shown. Consumers should typically
	 * wait until `status === 'done'` before showing an empty state, otherwise
	 * an empty state may flash on initial render while the placement is still
	 * loading.
	 */
	onMessageCountChange?: (numberOfMessages: number, status: AdminAgentRecommendationStatus) => void;
};

const placementConfig: PlacementBrowserConfig = {
	placementId: 'admin-agent-recommendation',
};

const placement = {
	createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	AdminAgentRecommendationProps
> = createFrontend<AdminAgentRecommendationProps>(
	(Messages) =>
		({ preloadedInitialData, onMessageCountChange, onSetUp, onLearnMore }) => {
			const [{ messages, status }, setMessagesState] = useState<{
				messages: BackendMessageBody<SerializableRecord>[];
				status: 'loading' | 'done';
			}>(() => {
				if (preloadedInitialData) {
					return {
						status: 'done',
						messages: preloadedInitialData?.messages ?? [],
					};
				}

				return { messages: [], status: 'loading' };
			});

			const isDone = status === 'done';

			const { data, error, isLoading } = usePlacementApi<DefaultMessagesResponse>('/', {
				autoRefetch: {
					isEnabled: !isDone,
					updateKey: 'admin-agent-recommendation',
				},
			});

			useEffect(() => {
				if (!isLoading && !isDone) {
					setMessagesState({
						messages: data?.messages ?? [],
						status: 'done',
					});
				}
			}, [isLoading, isDone, data]);

			const reportedMessageCount = messages.length;

			useEffect(() => {
				if (!onMessageCountChange) {
					return;
				}
				onMessageCountChange(reportedMessageCount, status);
			}, [reportedMessageCount, status, onMessageCountChange]);

			const handleDismiss = (messageInstanceId: string): void => {
				setMessagesState((prev) => ({
					status: 'done',
					messages: prev.messages.filter(
						(message) => messageInstanceId !== message.messageInstanceId,
					),
				}));
			};

			if (error || status === 'loading' || !messages?.length) {
				return null;
			}

			const messageToRender = messages[0];

			return messageToRender ? (
				<Messages.render
					key={messageToRender.messageInstanceId}
					dismiss={() => handleDismiss(messageToRender.messageInstanceId)}
					{...messageToRender}
					onSetUp={onSetUp}
					onLearnMore={onLearnMore}
				/>
			) : null;
		},
);
